/* eslint-disable */

import { parseAttribute, isNaN, dashToCamel, camelToDash, isObjectLike } from './util/AttributeParser';
import { getClosestParentCustomElementNode, isOfSameNodeType } from './util/DOMHelper';

/**
 * The base custom element that every other element should extend.
 */

class BaseElement extends HTMLElement {
    constructor(options = {}) {
        super();
        this.$refs = {};
        this._state = {};
        this._mutationObserver = null;
        this._registeredEvents = [];
        this._options = Object.assign(
            {
                autoRender: true,
                deferRender: true,
                propertyOptions: {},
            },
            options,
        );
    }

    connectedCallback() {
        // define all attributes to "this" as properties
        this.defineAttributesAsProperties();

        // define all properties to "this"
        this.defineProperties();

        // define all computed properties to "this"
        this.defineComputedProperties();

        // define everything that should be observed
        this.defineObserver();

        if (!this.hasAttribute('deferRender') && !this._options.deferRender) {
            if ('beforeRender' in this.hooks()) {
                this.hooks()['beforeRender']();
            }
            this.render();
        }

        // register events and refs
        this.registerEventsAndRefs();

        if ('connected' in this.hooks()) {
            this.hooks()['connected']();
        }
    }

    defineObserver() {
        // see: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver#Example_usage
        this._mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'attributes' && this._state.hasOwnProperty(dashToCamel(mutation.attributeName))) {
                    // update property by invoking the setter
                    this[dashToCamel(mutation.attributeName)] = parseAttribute(
                        this.getAttribute(mutation.attributeName),
                    );
                }
            });
        });

        this._mutationObserver.observe(this, { attributes: true });
    }

    disconnectedCallback() {
        // console.log('BaseComponent::disconnectedCallback()');

        // remove events for elements in the component
        this.removeEvents();

        // remove observers
        if (this._mutationObserver) this._mutationObserver.disconnect();

        if ('disconnected' in this.hooks()) {
            this.hooks()['disconnected']();
        }
    }

    /**
     * This should be called by the template component AFTER adding the template to
     * the DOM.
     * Here we will register the events and the refs for the element.
     */
    render() {
        this.registerEventsAndRefs();

        if ('afterRender' in this.hooks()) {
            this.hooks()['afterRender']();
        }
    }

    /**
     * Register events and refs for the component
     */
    registerEventsAndRefs() {
        // register events for elements in the component
        this.registerEvents();

        // register $refs
        this.registerRefs();
    }

    /**
     * Defines all attributes assigned in the HTML for the element as properties on the element
     * If the value for an attribute can be parsed to an object or array, it will do so
     */
    defineAttributesAsProperties() {
        const ignoreAttributes = ['class', 'style'];
        Array.from(this.attributes)
            .filter(attribute => {
                return !ignoreAttributes.includes(attribute.name);
            })
            .forEach(attribute => {
                this.defineProperty(dashToCamel(attribute.name), parseAttribute(attribute.value), true);
            });
    }

    /**
     * Properties to be defined and assigned on the element. Should be overwritten in child components
     * Properties defined here will also trigger a render() when changed.
     * eg. { stringProperty: 'value', numberProperty: 13, booleanProperty: true } }
     * @return {{}}
     */
    properties() {
        return {};
    }

    /**
     * Defines properties on the element based on keys from this.properties()
     * Will trigger render() when a property was changed
     */
    defineProperties() {
        Object.keys(this.properties()).forEach(prop => {
            // when mixing shadow dom elements with light dom elements and nesting custom elements
            // it might occur that properties where set on an element before it was
            // registered or connected. To avoid such timing issues we check
            // if a value was set for that specific property on the
            // prototype before assigning a default value
            const value = this[prop] || this.properties()[prop];
            this.defineProperty(prop, value);
        });
    }

    /**
     * Define a property on the element
     * Will trigger watch() function when a property was changed
     * Will trigger render() when a property was changed
     */
    defineProperty(property, value, reflectAttribute = false) {
        if (this._state.hasOwnProperty(property)) {
            // property has already been defined as an attribute nothing to do here
            return;
        }

        this._state[property] = value;

        Object.defineProperty(this, property, {
            get: () => {
                return this._state[property];
            },
            set: newValue => {
                const oldValue = this._state[property];
                const newValueString = JSON.stringify(newValue);

                if (JSON.stringify(oldValue) !== newValueString) {
                    this._state[property] = newValue;

                    if (reflectAttribute) {
                        if (newValue === undefined || newValue === null || isNaN(newValue)) {
                            // these would be reflected as strings: "undefined" || "null" || "NaN"
                            // which is not the desired behaviour. Therefore we reflect them as empty strings
                            this.setAttribute(camelToDash(property), '');
                        } else {
                            const attributeValue = isObjectLike(newValue) ? newValueString : newValue;
                            this.setAttribute(camelToDash(property), attributeValue);
                        }
                    }

                    if (this._options.autoRender && typeof this.render === 'function') {
                        if ('beforeRender' in this.hooks()) {
                            this.hooks()['beforeRender']();
                        }
                        this.render();
                    }

                    // notify watched properties (after render())
                    if (property in this.watch()) {
                        this.watch()[property](oldValue, newValue);
                    }

                    // dispatch change event
                    if (
                        property in this._options['propertyOptions'] &&
                        this._options['propertyOptions'][property]['notify'] === true
                    ) {
                        this.dispatch(`${camelToDash(property)}-changed`, newValue, true);
                    }
                }
                return this;
            },
        });
    }

    /**
     * Lifecycle hooks (callbacks) to be notified when triggered. Can be overwritten in child components
     * Available hooks:
     * {
     *  'connected': () => {},
        'beforeRender': () => {},
        'afterRender': () => {},
        'disconnected': () => {},
     * }
     * @return {{}}
     */
    hooks() {
        return {};
    }

    /**
     * Watched attributes & properties to be notified to the element when changed. Should be overwritten in child components
     * When an attribute or property as the key was changed on the element, the callback function defined as value
     * will be called with `newValue` and `oldValue`
     * eg. { property: (oldValue, newValue) => { console.log('property changed!, oldValue, newValue); } }
     * @return {{}}
     */
    watch() {
        return {};
    }

    /**
     * Computed properties to be calculated for the element. Should be overwritten in child components
     * Each key in the returned object will be available as variable in the context
     * Each value should be a function that returns something
     * eg. { computedProperty: () => { return 'value ' + 13 } }
     * @return {{}}
     */
    computed() {
        return {};
    }

    /**
     * Defines computed properties on the element based on keys from this.computed()
     */
    defineComputedProperties() {
        // console.log('BaseComponent::generateComputedProperties()', Object.keys(this.computed()));

        Object.keys(this.computed()).forEach(prop => {
            Object.defineProperty(this, prop, {
                get: () => this.computed()[prop](),
            });
        });
    }

    /**
     * Events to be registered for/on the element. Should be overwritten in child components
     * eg. { '[element-attribute]': {
     *               'click': (e) => {console.log('FooComponent::testButton 1 click()', e)},
     *               'mouseover': (e) => {console.log('FooComponent::testButton 1 mouseover()', e)},
     *           }
     *     }
     * @return {{}}
     */
    events() {
        return {};
    }

    /**
     * Registers events on the element based on this.events().
     * Removes all previously registered events
     */
    registerEvents() {
        // remove all previously registered events to prevent duplicate triggers
        this.removeEvents();

        // register events
        const eventDefinitions = this.events();
        Object.keys(eventDefinitions).forEach(elementSelector => {
            const events = eventDefinitions[elementSelector];
            const selectorWhiteList = {
                window,
                document,
                this: this.getRoot(),
            };
            const whiteListElement = selectorWhiteList[elementSelector];
            const eventTargets = whiteListElement
                ? [whiteListElement]
                : this.getRoot().querySelectorAll(elementSelector);

            eventTargets.forEach(eventTarget => {
                Object.keys(events).forEach(eventName => {
                    const callback = events[eventName].bind(this);
                    eventTarget.addEventListener(eventName, callback);
                    this._registeredEvents.push({
                        eventTarget,
                        eventName,
                        callback,
                    });
                });
            });
        });
    }

    /**
     * Removes all events from the element that where previously registered.
     */
    removeEvents() {
        this._registeredEvents.forEach(({ eventTarget, eventName, callback }) => {
            if (eventTarget === window || eventTarget === document || this.getRoot().contains(eventTarget)) {
                eventTarget.removeEventListener(eventName, callback);
            }
        });
        this._registeredEvents = [];
    }

    /**
     * Generates an object that holds child components & DOM nodes that have a ref attribute registered.
     * Will be available on the element as this.$refs
     */
    registerRefs() {
        const refsNodeList = this.getRoot().querySelectorAll('[ref]');
        const refsArray = Array.from(refsNodeList);
        const refsMap = {};

        refsArray.forEach(refNode => {
            const refKey = refNode.getAttribute('ref');
            refsMap[refKey] = refNode;
        });

        this.$refs = refsMap;
    }

    /**
     * Dispatches an custom event
     * @param name for the event to be dispatched
     * @param data to be send along with the event
     * @param bubble indicates if event bubbles or not
     * @param cancelable indicates if event is canceable or not
     */
    dispatch(name, data, bubble = false, cancelable = false) {
        const event = new CustomEvent(name, {
            bubbles: bubble,
            cancelable: cancelable,
            detail: data,
        });
        this.dispatchEvent(event);
    }

    getRoot() {
        return this.shadowRoot !== null ? this.shadowRoot : this;
    }
}

export { BaseElement };
